import { applicantStore } from '@/store'
import { BaseError } from '@/store/entities'
import { ErrorUtils } from '@/commons/utils/error'
import {
    PhotoValidationPayload,
    EmptyFingerprintResponse
} from '@/commons/types'
import { ENVIRONMENT } from '@/commons/configuration'
import axios, { AxiosResponse, CancelTokenSource } from 'axios'
import authenticatedAxios from '@/commons/configuration/authenticated.axios'
import {
    ErrorActivity,
    RequestErrorCode,
    RequestClientErrorStatus,
    RequestErrorMessageTitle,
    RequestErrorCodeIgnoreKey
} from '@/commons/constants/error'
import { ObjectUtils } from '@/commons/utils/objects'

export class BiometryService {
    private static isFingerprintsNotFound (errorEntity: BaseError): boolean {
        return (
            errorEntity.messageTranslationKeys.length === 1 &&
            errorEntity.messageTranslationKeys[0] === RequestErrorCode.FINGERPRINTS_NOT_FOUND
        )
    }

    private static isFingerprintsPersonTooYoung (errorEntity: BaseError): boolean {
        return (
            errorEntity.messageTranslationKeys.length === 1 &&
            errorEntity.messageTranslationKeys[0] === RequestErrorCodeIgnoreKey.FINGERPRINT_PERSON_TOO_YOUNG.toString()
        )
    }

    static fetchBiometry (personCode: string, cancelToken: CancelTokenSource): Promise<AxiosResponse> {
        return authenticatedAxios.get(ENVIRONMENT.biometryUrl, {
            params: {
                personCode: personCode,
                type: null
            },
            cancelToken: cancelToken.token
        })
    }

    static async fetchFingerprints (
        personCode: string,
        cancelToken: CancelTokenSource,
        applicationType: string): Promise<AxiosResponse | EmptyFingerprintResponse> {
        try {
            const result = await authenticatedAxios.get(ENVIRONMENT.biometryFingerprintsUrl, {
                params: {
                    personCode: personCode,
                    type: applicationType
                },
                cancelToken: cancelToken.token
            })

            if (!result.data.sessionTime) {
                const errorEntity = ErrorUtils.getRequestErrorEntity(undefined, RequestErrorMessageTitle.FINGERPRINTS_NOT_FOUND)
                errorEntity.messageTranslationKeys = [
                    RequestErrorCode.FINGERPRINTS_NOT_FOUND
                ]
                throw errorEntity
            }

            return result
        } catch (e) {
            const errorResponse = ErrorUtils.getRequestErrorResponse(e)
            if (!errorResponse) {
                throw e
            }

            const errorEntity = ErrorUtils.getRequestErrorEntity(errorResponse)
            if (BiometryService.isFingerprintsPersonTooYoung(errorEntity)) {
                return { data: null }
            }

            if (BiometryService.isFingerprintsNotFound(errorEntity)) {
                errorEntity.titleTranslationKey = RequestErrorMessageTitle.FINGERPRINTS_NOT_FOUND
            }

            throw errorEntity
        }
    }

    static async validatePhoto (personCode: string, image: string, applicationType: string): Promise<Array<string>> {
        const payload: PhotoValidationPayload = {
            personCode: personCode,
            data: image,
            applicationType: applicationType
        }
        try {
            applicantStore.requestCancelToken = axios.CancelToken.source()
            const response = await authenticatedAxios.post(ENVIRONMENT.awareUrl, payload, {
                cancelToken: applicantStore.requestCancelToken.token
            })
            applicantStore.requestCancelToken = null

            if (response && response.data && !ObjectUtils.isEmpty(response.data.errors)) {
                const error = ErrorUtils.getRequestErrorEntity(response)
                return error.messageTranslationKeys
            }
        } catch (e) {
            // TODO: Talk if this implementation is correct i.e for business side
            // meaning that should we block proceed button if aware loading
            if (axios.isCancel(e)) {
                return []
            }

            const response = ErrorUtils.getRequestErrorResponse(e)
            const error = ErrorUtils.getRequestErrorEntity(response)

            if (response && response.status === RequestClientErrorStatus.BAD_REQUEST) {
                return error.messageTranslationKeys
            } else {
                error.errorActivity = ErrorActivity.SHOW_ONLY_MESSAGE
                error.messageTranslationKeys = [
                    'errors.request.image.abis.message'
                ]
                ErrorUtils.requestErrorHandler(error)
            }
        }
        return []
    }
}
